import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router'
import { Button } from '#/components/ui/button'
import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '#/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '#/components/ui/form'
import { Input } from '#/components/ui/input'
import { AlertBox } from '#/components/ui/alert-box'
import { mutationError } from '#/lib/mutation-error'
import { organizationAPI } from '#/api/organization'
import { keycardAPI } from '#/api/keycard'


interface FormInputs {
  id: string
  friendlyId: string
  willBindToChargerId: string
}

export default function NewKeycardModal() {
  const { t } = useTranslation([ 'keycards', 'general' ])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const params = useParams() as { organization: string }

  const close = () => navigate('..')

  // Fetch the organization for longId
  const { data: organization } = useSuspenseQuery({
    queryKey: [ 'organizations', params.organization ],
    queryFn: () => organizationAPI.get(params.organization)
  })

  const form = useForm<FormInputs>({
    defaultValues: {
      id: '',
      friendlyId: '',
      willBindToChargerId: ''
    }
  })

  // Mutation to create keycard
  const mutation = useMutation({
    mutationFn: (data: FormInputs) => keycardAPI.create({
      organizationId: organization.id,
      ...data,
      willBindToChargerId: data.willBindToChargerId === '' ? undefined : data.willBindToChargerId
    }),
    onSuccess: created => {
      queryClient.setQueryData(
        [ 'organizations', params.organization, 'keycards', created.id ],
        created
      )
      navigate('..')
    },
    onError: () => {
      // handled via AlertBox
    }
  })

  return (
    <Dialog open modal onOpenChange={close}>
      <DialogContent className='max-h-[80%] overflow-y-scroll'>
        <DialogHeader>
          <DialogTitle>{t('new_keycard.title')}</DialogTitle>
          <DialogDescription>{t('new_keycard.description')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(values => mutation.mutate(values))}
            className="flex flex-col gap-4"
            autoComplete="off"
          >

            {mutation.error && (
              <AlertBox
                error
                title={mutationError(mutation.error, {
                  default: t('new_keycard.errors.default'),
                  forbidden: t('new_keycard.errors.forbidden')
                })}
              />
            )}

            {/* Id */}
            <FormField
              name="id"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.id')}</FormLabel>
                  <FormControl>
                    <Input
                      required
                      maxLength={20}
                      autoComplete="off"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Vendor */}
            <FormField
              name="friendlyId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.friendlyId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Will Bind To Charger Id */}
            <FormField
              name="willBindToChargerId"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.willBindToChargerId')}</FormLabel>
                  <FormControl>
                    <Input
                      maxLength={128}
                      autoComplete="off"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter className="gap-2 justify-start">
              <Button type="button" variant="outline" onClick={close}>
                {t('general:cancel')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {t('new_keycard.submit')}
              </Button>
            </DialogFooter>

          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
